import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  putPlannerTodosNotes,
  postPlannerTodosNotes,
  deletePlannerTodosNotes,
} from "../../../services/apiPlannerTodos";
import { usePlannerGlobalNotes } from "../../../reactQuery/plannerTodos";
import { localStorageInstance } from "../../../utils/localStorage";
import NoteEditor from "./components/NoteEditor";
import NotePopover from "./components/NotePopover";
import AddIcon from "@mui/icons-material/Add";
import AddPlannerNotes from "../../../styles/svg/add-planner-notes.svg";
import DynamicColorNote from "./components/DynamicColorNote";
import "./GlobalNotesList.scss";
import { newFormatDate } from "../../../utils/helpers";

/**
 * Component to display and manage a list of global notes
 */
const GlobalNotesList = ({ compact = false, globalNotes=[], isRefetching }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeNote, setActiveNote] = useState({
    id: null,
    content: "",
    color_code: "", // Default color (Soft Yellow)
    created_at: "",
  });
  
  const [globalNotesList, setGlobalNotesList] = useState([]);
  
  // State for note preview popover
  const [openNotePreview, setOpenNotePreview] = useState(false);
  const [noteAnchorEl, setNoteAnchorEl] = useState(null);
  const [previewNote, setPreviewNote] = useState(null);
  
  const queryClient = useQueryClient();
  // const { error, globalNotes, isError,isRefetching } = usePlannerGlobalNotes();

  useEffect(() => {
    if (globalNotes) {
      const notes = globalNotes?.data
        ?.filter(
          (item) =>
            item.user_id === localStorageInstance.getItem("planner_user_id")
        )
        .filter((item) => item.todo_id === null);

      if (notes && notes.length > 0) {
        setGlobalNotesList(notes);
      }else{
        setGlobalNotesList([]);
      }
    }
  }, [globalNotes, isRefetching]);

  // Create global note mutation
  const { mutate: createNote } = useMutation({
    mutationFn: postPlannerTodosNotes,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["globalNotes"] });
      setIsLoading(false);
      toast.success("Note created successfully");
      setIsOpen(false);
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to create note");
      setIsLoading(false);
    },
  });

  // Update global note mutation
  const { mutate: updateNote } = useMutation({
    mutationFn: putPlannerTodosNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["globalNotes"] });
      setIsLoading(false);
      toast.success("Note updated successfully");
      setIsOpen(false);
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to update note");
      setIsLoading(false);
    },
  });

  // Delete note mutation
  const { mutate: deleteNote } = useMutation({
    mutationFn: deletePlannerTodosNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["globalNotes"] });
      setIsLoading(false);
      toast.success("Note deleted successfully");
      setIsOpen(false);
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to delete note");
      setIsLoading(false);
    },
  });

  const handleCreateNewNote = () => {
    setActiveNote({
      id: null,
      title: "",
      content: "",
      color_code: "",
      created_at: "",
    });
    setIsOpen(true);
  };

  const handleEditNote = (note) => {
    setActiveNote({
      id: note.id,
      title: note.title || "",
      content: note.content || "",
      color_code: note?.color_code || "",
      created_at: note.created_at || new Date().toLocaleDateString(),
    });
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    handleSave();
  };

  const handleSave = () => {
    if (!activeNote.content.trim() && !activeNote.title) {
      // toast.error("Cannot save empty note");
      return;
    }
    
    setIsLoading(true);

    if (activeNote.id) {
      // If we have a note ID, update the existing note
      function hasOnlyHtmlTags(input) {
        const textOnly = input.replace(/<[^>]*>/g, "");
        return textOnly.trim().length === 0;
      }
      const isEmptyHTML = hasOnlyHtmlTags(activeNote?.content || '');

      if (!activeNote.content.trim() || isEmptyHTML) {
        deleteNote(activeNote.id);
      } else {
        updateNote({
          id: activeNote.id,
          data: {
            content: activeNote.content,
            title: activeNote.title || '',
            color_code: activeNote.color_code,
            ...(activeNote.color_code ? { color_code: activeNote.color_code } : {}),
          },
        });
      }
    } else {
      // Otherwise, create a new note
      function hasOnlyHtmlTags(input) {
        const textOnly = input.replace(/<[^>]*>/g, "");
        return textOnly.trim().length === 0;
      }
      const isEmptyHTML = hasOnlyHtmlTags(activeNote?.content || '');

      if (!activeNote.title.trim() && isEmptyHTML) {
        setIsLoading(false);
        setIsOpen(false);
        return;
      } else {
        createNote({
          content: activeNote.content,
          title: activeNote.title || '',
          ...(activeNote.color_code
            ? { color_code: activeNote.color_code }
            : {}),
          todo_id: null,
        });
      }
    }
  };

  const handleDelete = () => {
    if (!activeNote.id) {
      // If there's no note ID, just close the dialog
      setIsOpen(false);
      return;
    }
    
    setIsLoading(true);
    // Delete the note via API
    deleteNote(activeNote.id);
  };

  // Function to extract a preview of the note content
  const getNotePreview = (content, maxLength = 150) => {
    // Remove HTML tags and get plain text
    const plainText = content.replace(/<[^>]*>/g, '');
    if (plainText.length <= maxLength) {
      return plainText;
    }
    return plainText.substring(0, maxLength) + '...';
  };

  // Handle mouse hover for note preview
  const handleNoteMouseEnter = (event, note) => {
    setNoteAnchorEl(event.currentTarget);
    setPreviewNote(note);
    setOpenNotePreview(true);
  };

  // Handle mouse leave for note preview
  const handleNoteMouseLeave = () => {
    setOpenNotePreview(false);
    setNoteAnchorEl(null);
    setPreviewNote(null);
  };

  // Get the last created note or null if no notes
  const lastNote = globalNotesList.length > 0 
    ? globalNotesList.reduce((latest, note) => 
        new Date(note.created_at) > new Date(latest.created_at) ? note : latest, 
        globalNotesList[0])
    : null;

  // Full view (shows the grid of notes)
  return (
    <div className="global-notes-list-container">
      {globalNotesList.length === 0 ? (
        <div className="notes-grid">
          <img
            src={AddPlannerNotes}
            alt="add-global-notes"
            onClick={handleCreateNewNote}
            className="cursor-pointer add-global-notes-icon"
          />
        </div>
      ) : (
        <div className="notes-grid">
          {/* {globalNotesList.map((note) => (
            <div
              className="global-note-button mr-1"
              key={note.id}
              onClick={() => handleEditNote(note)}
              onMouseEnter={(e) => handleNoteMouseEnter(e, note)}
              onMouseLeave={handleNoteMouseLeave}
            >
              <DynamicColorNote fill={note?.color_code} />
            </div>
          ))} */}
          <div>
            <img
              src={AddPlannerNotes}
              alt="add-global-notes"
              onClick={handleCreateNewNote}
              className="cursor-pointer add-global-notes-icon"
            />
          </div>
        </div>
      )}

      {/* Note Editor Dialog */}
      {isOpen && <NoteEditor
        open={isOpen}
        onClose={handleClose}
        onSave={handleSave}
        onDelete={handleDelete}
        noteData={activeNote}
        onNoteChange={setActiveNote}
        isLoading={isLoading}
        width="591px"
      />}

      {/* Note Preview Popover */}
      <NotePopover
        open={openNotePreview}
        anchorEl={noteAnchorEl}
        onClose={handleNoteMouseLeave}
        content={previewNote?.content || ""}
        date={newFormatDate(previewNote?.created_at)}
        backgroundColor={previewNote?.color_code || "#fff9c4"}
      />
    </div>
  );
};

export default GlobalNotesList; 